import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useTenantQuery } from '@/hooks/useTenant';
import { setLastSlug } from '@/lib/lastSlug';
import type { TenantConfig } from '@/types/tenant';

interface TenantContextValue {
  tenant: TenantConfig;
  slug: string;
}

const TenantContext = createContext<TenantContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used inside <TenantProvider>');
  return ctx;
}

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error, refetch } = useTenantQuery(slug);

  useEffect(() => {
    if (slug && data) {
      setLastSlug(slug);
    }
  }, [slug, data]);

  if (!slug) {
    return <ErrorPanel message="Missing client slug in URL." />;
  }
  if (isLoading) {
    return <LoadingPanel label="Loading client…" />;
  }
  if (error || !data) {
    // 2026-06-29 (NotificationClick502) — was a dead-end (no retry). On a GHL
    // throttle the global transient-retry already retries this fetch; the button
    // is the final manual backstop so the owner never has to force-close.
    return (
      <ErrorPanel message={`Could not load client "${slug}".`} onRetry={() => void refetch()} />
    );
  }

  return (
    <TenantContext.Provider value={{ tenant: data, slug }}>{children}</TenantContext.Provider>
  );
}

function LoadingPanel({ label }: { label: string }) {
  return (
    <div className="flex h-screen items-center justify-center bg-ink-50 text-ink-500 text-sm">
      {label}
    </div>
  );
}

function ErrorPanel({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3 bg-ink-50 px-6 text-center text-sm text-ink-700">
      <div>{message}</div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="ss-press rounded-lg bg-ss-blue px-4 py-2 text-sm font-medium text-white"
        >
          Try again
        </button>
      )}
    </div>
  );
}
